// Heuristic language detection. Tuned for the app's two supported languages
// (English + Polish) — used to pre-tag articles before sending them to the
// AI and to validate that the AI's cleaned output didn't drift to a
// different language than the original.
//
// We deliberately stop at a 2-class classifier with a "unknown" fallback
// because anything fancier would need a heavy library and this signal only
// has to be good enough to spot obvious cross-language leakage.

export type DetectedLang = 'pl' | 'en' | 'unknown';

// Polish-specific characters. None of these appear in standard English
// text; if they show up in cleaned output for an originally-English
// article, the AI translated against instructions.
const POLISH_CHARS_RE = /[ąćęłńóśźż]/gi;

// Polish function words / very common short tokens. Used to catch Polish
// titles that happen to omit diacritics (e.g. transliterated headlines).
// Conservative list — only words that are extremely unlikely to appear
// in an English headline.
const POLISH_WORD_RE = /\b(się|jest|że|oraz|który|która|które|przed|wśród|jednak|zostal|został|została|zostały|ponieważ|natomiast|właśnie)\b/i;

// "Strong" English-only function words. Rare in Polish.
const ENGLISH_WORD_RE = /\b(the|and|of|to|for|with|from|that|which|been|being|have|has|will|would|could|should|after|before|during|while|because)\b/i;

export const detectLang = (text: string | undefined | null): DetectedLang => {
  if (!text) return 'unknown';
  const trimmed = text.trim();
  if (trimmed.length === 0) return 'unknown';

  if (POLISH_CHARS_RE.test(trimmed)) return 'pl';
  // Reset lastIndex since the regex has the /g flag.
  POLISH_CHARS_RE.lastIndex = 0;

  if (POLISH_WORD_RE.test(trimmed)) return 'pl';
  if (ENGLISH_WORD_RE.test(trimmed)) return 'en';

  // No strong signal either way — keep "unknown" so callers can decide
  // not to enforce a strict match.
  return 'unknown';
};

// True when `cleaned` is in a language that's clearly different from
// `original`. "unknown" on either side counts as no-mismatch — we don't
// want to throw away usable cleanings when the heuristic just can't tell.
export const isLanguageMismatch = (original: string, cleaned: string): boolean => {
  const a = detectLang(original);
  const b = detectLang(cleaned);
  if (a === 'unknown' || b === 'unknown') return false;
  return a !== b;
};
